import { DataObject, DataObjectFactory } from "@/v2/models/base-data-object.js";
import { now } from "@/v2/utils/date.js";
import { CompanyFactory, CompanyMapperParameter } from "@/v2/models/companies";
import { ProductFactory, ProductMapperParameter } from "@/v2/models/products";
import { TierFactory, TierMapperParameter } from "@/v2/models/tiers.js";
import { MediaFactory, MediaMapperParameter } from "@/v2/models/medias.js";
import { ProductGroupFactory, ProductGroupMapperParameter } from "@/v2/models/product-groups.js";
import { Parameter } from "@/v2/models/parameters.js";

export class Coupon extends DataObject {
    _type = "coupon";

    id;
    code;
    name;
    description;
    type;
    discount_type;
    discount;
    point_needed;
    start_at;
    end_at;
    image_id;
    max_quantity_limit;
    max_amount_limit;
    max_redemption_limit;
    min_document_amount;
    is_accounts_excluded;
    is_tiers_excluded;
    is_products_excluded;
    is_product_groups_excluded;
    is_redeemable;
    is_redemption_required;
    individual_use;
    are_bundles_excluded;
    quantity = 0;
    created_by;
    created_at;
    updated_at;
    image;
    tiers = {};
    products = {};
    companies = {};
    product_groups = {};
    minimum_product_quantity;
    maximum_product_quantity;
    remaining_product_quantity;
    variation_quantity_limit;
    variation_quantity_remaining;
    promotion_id;
    promotion_line_id;
    is_trade_promotion_managed;
    promotion_code;
    promotion_version;
    promotion_detail_url;
    applied_discount_amount;

    getStatus() {
        const currentDate = now();
        if (currentDate > this.end_at) {
            return "expired";
        }
        if (currentDate < this.start_at) {
            return "upcoming";
        }
        return "ongoing";
    }
    tierIds;
    productIds;
    productGroupIds;
    companyIds;
}

export class CouponMapperParameter extends Parameter {
    productMapperParameter = new ProductMapperParameter();
    companyMapperParameter = new CompanyMapperParameter();
    tierMapperParameter = new TierMapperParameter();
    mediaMapperParameter = new MediaMapperParameter();
    productGroupMapperParameter = new ProductGroupMapperParameter();
    couponMapper;
}

export class CouponFactory {
    /**
     * Creates a new instance of a DataObject.
     * @function
     * @static
     * @param {Object, null} data Data to construct the Coupon object with. Also creates a product object using MetaFactory if data is not null.
     * @returns {Notification}
     */
    static construct(couponMapperParameter, data = null) {
        if (data != null) {
            let coupon = DataObjectFactory.getInstance().constructWithMapper(
                Coupon,
                couponMapperParameter.couponMapper,
                data
            );
            if (data.products != null) {
                let products = {};
                data.products.forEach((productData) => {
                    const product = ProductFactory.construct(couponMapperParameter.productMapperParameter, productData);
                    products[product.id] = product;
                });

                coupon.products = products;
            }

            if (data.companies != null) {
                let companies = {};
                data.companies.forEach((companyData) => {
                    const company = CompanyFactory.construct(couponMapperParameter.companyMapperParameter, companyData);
                    companies[company.id] = company;
                });

                coupon.companies = companies;
            }

            if (data.tiers != null) {
                let tiers = {};
                data.tiers.forEach((tierData) => {
                    const tier = TierFactory.construct(couponMapperParameter.tierMapperParameter, tierData);
                    tiers[tier.id] = tier;
                });

                coupon.tiers = tiers;
            }

            if (data.image != null) {
                const media = MediaFactory.construct(couponMapperParameter.mediaMapperParameter, data.image);

                coupon.image = media;
            }

            if (data.product_groups != null) {
                let product_groups = [];
                data.product_groups.forEach((productGroupData) => {
                    const productGroup = ProductGroupFactory.construct(
                        couponMapperParameter.productGroupMapperParameter,
                        productGroupData
                    );
                    product_groups.push(productGroup);
                });

                coupon.product_groups = product_groups;
            }

            return coupon;
        }

        return null;
    }
}
